'use client';
import { useEffect, useRef, useState } from 'react';
import { NodeViewWrapper, ReactNodeViewRenderer, type NodeViewProps } from '@tiptap/react';
import { VBlockShell } from '@/components/vBlock/vBlockShell';
import { getSignedUrl } from '@/lib/supabase/storageQueries';

function DocumentMediaBlock({ node, editor, updateAttributes, deleteNode }: NodeViewProps) {
  const blockId = useRef(crypto.randomUUID()).current;
  const path = node.attrs.attachment?.path as string | undefined;
  const [url, setUrl] = useState(node.attrs.src || node.attrs.url || '');
  const [error, setError] = useState('');
  const image = node.type.name === 'imageBlock';
  const mode = node.attrs.displayMode || (image ? 'preview' : 'file');
  const filename = node.attrs.attachment?.filename || node.attrs.filename || node.attrs.alt || 'קובץ';
  const width = node.attrs.freePosition?.width || node.attrs.displayWidth || 360;
  const height = mode === 'name' ? 40 : mode === 'file' ? 100 : node.attrs.displayHeight || 260;
  useEffect(() => {
    if (!path) { setUrl(node.attrs.src || node.attrs.url || ''); return; }
    let live = true;
    const refresh = async () => {
      const signed = await getSignedUrl(path);
      if (!live) return;
      if (signed) { setUrl(signed); setError(''); } else { setUrl(''); setError('אין אפשרות לפתוח את הקובץ כרגע'); }
    };
    void refresh(); const timer = setInterval(refresh, 30 * 60 * 1000);
    return () => { live = false; clearInterval(timer); };
  }, [path, node.attrs.src, node.attrs.url]);
  const setMode = (value: string) => { if (editor.isEditable) updateAttributes({ displayMode: value }); };
  return <NodeViewWrapper className="gam-document-media" data-media-mode={mode} contentEditable={false}>
    <VBlockShell blockId={blockId} title={filename} icon={image ? '▧' : '📎'}
      size={{ width, height }} minSize={{ width: 80, height: 40 }} maxSize={{ width: 2400, height: 2000 }}
      resizable={editor.isEditable && mode === 'preview'}
      contextActions={editor.isEditable ? [
        ...(image ? [{ id: 'preview', label: 'תמונה מלאה', onClick: () => setMode('preview') }] : []),
        { id: 'file', label: 'כרטיס קובץ', onClick: () => setMode('file') },
        { id: 'name', label: 'שורת שם קובץ', onClick: () => setMode('name') },
      ] : []}
      onEvent={event => {
        if (!editor.isEditable) return;
        if (event.type === 'block.resized') updateAttributes({ displayWidth: event.size.width, displayHeight: event.size.height,
          ...(node.attrs.freePosition ? { freePosition: { ...node.attrs.freePosition, width: event.size.width } } : {}) });
        if (event.type === 'block.context.action' && event.actionId === 'delete') deleteNode();
      }}>
      {error ? <span role="alert">{error}</span> : !url ? <span role="status">טוען קובץ…</span> :
        mode === 'preview' && image ? <img src={url} alt={node.attrs.alt || filename} style={{ width: '100%', height: '100%', objectFit: 'contain' }} /> :
          <a href={url} target="_blank" rel="noopener noreferrer" className="gam-media-file-link">{filename}{mode === 'file' && node.attrs.attachment?.bytes ? ` · ${Math.ceil(node.attrs.attachment.bytes / 1024)} KB` : ''}</a>}
    </VBlockShell>
  </NodeViewWrapper>;
}
export const documentMediaNodeView = ReactNodeViewRenderer(DocumentMediaBlock);
